import { Button } from "@zaphub/ui";
import { LogOut } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { acessoAtivo, useImpersonacao } from "./impersonacao";

/**
 * "Encerrar acesso" para o banner de impersonação do `PainelLayout`:
 *
 *   {impersonadoPor && (<div role="status" …>Você está acessando como … <BotaoEncerrarAcesso /></div>)}
 *
 * Também encerra sozinho quando o token de 30 minutos expira, devolvendo a sessão do administrador
 * em vez de deixar o painel cair em erro 401.
 */
export function BotaoEncerrarAcesso({ className }: { className?: string }) {
  const { encerrar } = useImpersonacao();
  const [saindo, setSaindo] = useState(false);
  const emAndamento = useRef(false);

  const sair = async (expirou: boolean) => {
    if (emAndamento.current) return;
    emAndamento.current = true;
    setSaindo(true);
    try {
      const r = await encerrar();
      if (!r.restaurada) toast.error("Sua sessão de administrador expirou. Entre de novo.");
      else if (expirou) toast.info("O acesso como cliente expirou (30 minutos). Você voltou à plataforma.");
      else toast.success("Acesso encerrado. Você voltou à plataforma.");
    } finally {
      emAndamento.current = false;
      setSaindo(false);
    }
  };

  const expiraEm = acessoAtivo()?.expiraEm;
  useEffect(() => {
    if (!expiraEm) return;
    const restante = new Date(expiraEm).getTime() - Date.now();
    const timer = setTimeout(() => void sair(true), Math.max(0, restante));
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `sair` só usa refs e o hook estável
  }, [expiraEm]);

  return (
    <Button
      variante="contorno"
      tamanho="sm"
      className={
        className ??
        "ml-3 min-h-11 border-warning-foreground/50 bg-transparent text-warning-foreground hover:bg-warning-foreground/10 sm:min-h-8"
      }
      onClick={() => void sair(false)}
      disabled={saindo}
    >
      <LogOut aria-hidden="true" />
      {saindo ? "Encerrando…" : "Encerrar acesso"}
    </Button>
  );
}
